// FieldDesk — экран «Обзор» (Sprint 7-redesign).
//
// Сверху — строка KPI за сегодня (всего / выполнено / в работе / ожидают /
// отменено / с опозданием). Ниже секции со счётчиком + список выездов:
//   1) Выездов сегодня      — все статусы за сегодня
//   2) В работе сейчас      — EN_ROUTE + ON_SITE (наши «in_progress»)
//   3) Выполнено сегодня    — COMPLETED + scheduledDate == today
//   4) Отменено сегодня     — CANCELLED + scheduledDate == today
//   5) Не закрыты           — прошлые 7 дней, статус не COMPLETED/CANCELLED
//   6) Запланировано завтра — PLANNED + scheduledDate == tomorrow
//
// Прежний layout (4 KPI-карточки + 2 списка) удалён. Если когда-то понадобится —
// смотреть git history до Sprint 7.
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../auth/AuthContext';
import type { VisitDto } from '../api/types';
import { TaskCard } from '../components/TaskCard';

interface Props {
  onAdd(): void;
  onOpenVisit(v: VisitDto): void;
}

export function DashboardScreen({ onAdd, onOpenVisit }: Props) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [todayVisits, setTodayVisits] = useState<VisitDto[]>([]);
  const [tomorrowVisits, setTomorrowVisits] = useState<VisitDto[]>([]);
  const [pastVisits, setPastVisits] = useState<VisitDto[]>([]);

  useEffect(() => {
    const today = new Date();
    const tomorrow = new Date(today.getTime() + 864e5);
    const yesterday = new Date(today.getTime() - 864e5);
    const weekAgo = new Date(today.getTime() - 7 * 864e5);
    const todayStr = format(today, 'yyyy-MM-dd');
    const tomorrowStr = format(tomorrow, 'yyyy-MM-dd');
    Promise.all([
      api.listVisits({ from: todayStr, to: todayStr, limit: 200 }),
      api.listVisits({ from: tomorrowStr, to: tomorrowStr, limit: 200 }),
      api.listVisits({ from: format(weekAgo, 'yyyy-MM-dd'), to: format(yesterday, 'yyyy-MM-dd'), limit: 500 }),
    ])
      .then(([t, tm, past]) => {
        setTodayVisits(t.items);
        setTomorrowVisits(tm.items);
        setPastVisits(past.items);
      })
      .catch((e: Error) => setErr(e.message))
      .finally(() => setLoading(false));
  }, [api]);

  // Партиции «сегодня» по статусу. EN_ROUTE+ON_SITE = «в работе сейчас».
  const inProgress = todayVisits.filter(
    (v) => v.status === 'EN_ROUTE' || v.status === 'ON_SITE',
  );
  const completedToday = todayVisits.filter((v) => v.status === 'COMPLETED');
  const cancelledToday = todayVisits.filter((v) => v.status === 'CANCELLED');
  const waitingToday = todayVisits.filter((v) => v.status === 'PLANNED');
  const lateToday = todayVisits.filter((v) => v.wasLate === true);
  // Хвосты прошлых дней: выезд так и не закрыли (не выполнен и не отменён).
  const unclosedPast = pastVisits.filter((v) => v.status !== 'COMPLETED' && v.status !== 'CANCELLED');
  // Завтра показываем только PLANNED — если уже EN_ROUTE/COMPLETED завтрашним
  // числом, что-то странное произошло, не наш кейс для дашборда.
  const plannedTomorrow = tomorrowVisits.filter((v) => v.status === 'PLANNED');
  // Процент выполнения считаем без отменённых — они не зависят от инженера.
  const activeToday = todayVisits.length - cancelledToday.length;
  const completionPct = activeToday > 0 ? Math.round((completedToday.length / activeToday) * 100) : null;

  return (
    <div style={{ padding: 24, height: '100%', overflow: 'auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 700 }}>Обзор</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            {format(new Date(), 'd MMMM yyyy')} · {format(new Date(), 'EEEE')}
          </p>
        </div>
        <button
          onClick={onAdd}
          style={{
            padding: '9px 18px',
            background: '#2563eb',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          + Новый выезд
        </button>
      </div>

      {err && <ErrorStrip message={err} />}

      {loading ? (
        <SkeletonSections />
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
              gap: 10,
              marginBottom: 14,
            }}
          >
            <Kpi label="Всего сегодня" value={todayVisits.length} tone="#2563eb" />
            <Kpi
              label="Выполнено"
              value={completedToday.length}
              tone="#16a34a"
              hint={completionPct != null ? `${completionPct}% от активных` : undefined}
            />
            <Kpi label="В работе" value={inProgress.length} tone="#7c3aed" />
            <Kpi label="Ожидают выезда" value={waitingToday.length} tone="#0891b2" />
            <Kpi label="Отменено" value={cancelledToday.length} tone="#dc2626" />
            <Kpi label="С опозданием" value={lateToday.length} tone="#d97706" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 14 }}>
            <Section
              badge={todayVisits.length}
              title="Выездов сегодня"
              tone="#2563eb"
              visits={todayVisits}
              onOpenVisit={onOpenVisit}
            />
            <Section
              badge={inProgress.length}
              title="В работе сейчас"
              tone="#7c3aed"
              visits={inProgress}
              onOpenVisit={onOpenVisit}
            />
            <Section
              badge={completedToday.length}
              title="Выполнено сегодня"
              tone="#16a34a"
              visits={completedToday}
              onOpenVisit={onOpenVisit}
            />
            <Section
              badge={cancelledToday.length}
              title="Отменено сегодня"
              tone="#dc2626"
              visits={cancelledToday}
              onOpenVisit={onOpenVisit}
              renderNote={(v) => (v.cancelReason ? `Причина: ${v.cancelReason}` : null)}
            />
            <Section
              badge={unclosedPast.length}
              title="Не закрыты (прошлые 7 дней)"
              tone="#d97706"
              visits={unclosedPast}
              onOpenVisit={onOpenVisit}
              emptyText="Хвостов нет"
            />
            <Section
              badge={plannedTomorrow.length}
              title="Запланировано на завтра"
              tone="#0891b2"
              visits={plannedTomorrow}
              onOpenVisit={onOpenVisit}
            />
          </div>
        </>
      )}
    </div>
  );
}

// --- KPI-плашка: число + подпись (+ необязательная подсказка) ---------------
function Kpi({ label, value, tone, hint }: { label: string; value: number; tone: string; hint?: string }) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: '12px 14px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
        borderTop: `3px solid ${tone}`,
      }}
    >
      <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: tone, lineHeight: 1.1 }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{hint}</div>}
    </div>
  );
}

// --- Секция: бейдж-счётчик + список карточек выездов -----------------------
function Section({
  badge,
  title,
  tone,
  visits,
  onOpenVisit,
  renderNote,
  emptyText = 'Нет выездов',
}: {
  badge: number;
  title: string;
  tone: string;
  visits: VisitDto[];
  onOpenVisit(v: VisitDto): void;
  /** Доп. строка под карточкой (например, причина отмены). */
  renderNote?(v: VisitDto): string | null;
  emptyText?: string;
}) {
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 18,
        boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 28,
            height: 28,
            padding: '0 8px',
            borderRadius: 999,
            background: tone + '18',
            color: tone,
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          {badge}
        </span>
        <h2 style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{title}</h2>
      </div>
      {visits.length === 0 ? (
        <Empty>{emptyText}</Empty>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {visits.slice(0, 10).map((v) => {
            const note = renderNote?.(v);
            return (
              <div key={v.id}>
                <TaskCard visit={v} onClick={onOpenVisit} compact />
                {note && <div style={{ fontSize: 11, color: '#64748b', padding: '3px 2px 0 14px' }}>{note}</div>}
              </div>
            );
          })}
          {visits.length > 10 && (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
              … и ещё {visits.length - 10}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div style={{ fontSize: 13, color: '#94a3b8', padding: '20px 0' }}>{children}</div>;
}

function SkeletonSections() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 14 }}>
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          style={{
            height: 200,
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
          }}
        />
      ))}
    </div>
  );
}

// Используется в других экранах (Engineers / Map / Reports / Schedule) — экспорт сохраняем.
export function ErrorStrip({ message }: { message: string }) {
  return (
    <div
      style={{
        padding: '10px 14px',
        background: '#fef2f2',
        color: '#b91c1c',
        borderRadius: 8,
        fontSize: 13,
        marginBottom: 14,
      }}
    >
      ⚠ {message}
    </div>
  );
}
